/**
 * deriveTheme — turn a client's brand inputs into a full `ThemeTokens` first pass.
 *
 * A brand usually hands you ONE or TWO anchor colors (a primary, maybe a navy
 * secondary + a gold). But a theme needs ~12 tokens. Most are DERIVABLE from the
 * primary (foregrounds by contrast, a darker hover shade, a light tint, the ring,
 * a brand gradient); the brand-specific extras (secondary, highlight, surfaces,
 * fonts, logo) are passed in. Output seeds `tenant_config.theme` directly; the
 * super-admin then fine-tunes by eye in the Layout & Theme editor.
 *
 * This is a FIRST PASS, not a final commitment — see docs/CLIENT_ONBOARDING.md.
 */
import type { ThemeTokens } from './theme';

export type DeriveThemeInput = {
  /** Primary brand color — hex (`#43a047`) or HSL triplet (`123 46% 42%`). Required. */
  primary: string;
  /** Explicit secondary brand color (e.g. a navy). Omit to leave at baseline. */
  secondary?: string;
  /** Explicit highlight / gold accent. Omit to leave at baseline. */
  highlight?: string;
  /** Heading + body font CSS stacks. */
  fontHeading?: string;
  fontBody?: string;
  /** Stylesheet hrefs for the chosen fonts (Google Fonts etc.). */
  fontLinks?: string[];
  logoUrl?: string;
  logoUrlDark?: string;
  /** Text wordmark fallback when there's no logo. */
  wordmark?: string;
  /** Corner radius, raw CSS (e.g. "0.5rem"). */
  radius?: string;
  /** Override the derived brand gradient with explicit CSS. */
  brandGradient?: string;
};

type Hsl = { h: number; s: number; l: number };

const clamp = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, n));
const round1 = (n: number) => Math.round(n * 10) / 10;

function hexToRgb(hex: string): [number, number, number] {
  let h = hex.replace('#', '').trim();
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

function rgbToHsl(r: number, g: number, b: number): Hsl {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;
  if (d !== 0) {
    s = d / (1 - Math.abs(2 * l - 1));
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  return { h: round1(h), s: round1(s * 100), l: round1(l * 100) };
}

/** Accepts a hex string or an HSL triplet and returns parsed HSL. */
export function parseColor(color: string): Hsl {
  const c = color.trim();
  if (c.startsWith('#')) {
    const [r, g, b] = hexToRgb(c);
    return rgbToHsl(r, g, b);
  }
  const m = c.match(/^([\d.]+)\s+([\d.]+)%\s+([\d.]+)%$/);
  if (m) return { h: Number(m[1]), s: Number(m[2]), l: Number(m[3]) };
  throw new Error(`Unrecognized color: "${color}" (use hex or "H S% L%")`);
}

const triplet = (c: Hsl) => `${round1(c.h)} ${round1(c.s)}% ${round1(c.l)}%`;
const toTriplet = (color: string) => triplet(parseColor(color));

/** WCAG relative luminance of an HSL color (0..1), for contrast decisions. */
function luminance(c: Hsl): number {
  const C = (1 - Math.abs((2 * c.l) / 100 - 1)) * (c.s / 100);
  const X = C * (1 - Math.abs(((c.h / 60) % 2) - 1));
  const m = c.l / 100 - C / 2;
  let r = 0;
  let g = 0;
  let b = 0;
  if (c.h < 60) [r, g, b] = [C, X, 0];
  else if (c.h < 120) [r, g, b] = [X, C, 0];
  else if (c.h < 180) [r, g, b] = [0, C, X];
  else if (c.h < 240) [r, g, b] = [0, X, C];
  else if (c.h < 300) [r, g, b] = [X, 0, C];
  else [r, g, b] = [C, 0, X];
  const lin = (v: number) => {
    const x = v + m;
    return x <= 0.03928 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

/**
 * "0 0% 100%" (white) or "0 0% 13%" (near-black ink) for text ON this color.
 * Biased toward WHITE (brand convention for buttons): ink is chosen only for
 * genuinely LIGHT colors (gold, mint, pastels) where white would wash out.
 * Pure WCAG-max-contrast would pick ink on mid-tones like a brand green, which
 * contradicts how brands actually style their primary button — so we threshold
 * on luminance (~0.5) instead.
 */
export function readableForeground(c: Hsl): string {
  return luminance(c) > 0.5 ? '0 0% 13%' : '0 0% 100%';
}

/**
 * Derive a full ThemeTokens first pass from brand inputs. Tokens that aren't
 * derivable from the primary (secondary, highlight, surfaces, pv) are emitted
 * ONLY when supplied — otherwise they fall through to the Omni baseline.
 */
export function deriveTheme(input: DeriveThemeInput): ThemeTokens {
  const p = parseColor(input.primary);
  const primaryFg = readableForeground(p);

  // Hover-dark: relatively darker, same hue/sat (≈ -18% lightness).
  const dark: Hsl = { h: p.h, s: p.s, l: clamp(round1(p.l * 0.82), 10, p.l - 4) };
  // Light tint: very light, slightly punchier saturation (for accent surfaces).
  const light: Hsl = { h: p.h, s: clamp(round1(p.s + 12), 25, 75), l: 94 };

  const out: ThemeTokens = {
    primary: triplet(p),
    primaryForeground: primaryFg,
    accent: triplet(light),
    accentForeground: triplet(dark),
    ring: triplet(p),
    brandDark: triplet(dark),
    brandLight: triplet(light),
    brandGradient:
      input.brandGradient ??
      `linear-gradient(120deg, hsl(${triplet({ h: p.h, s: p.s, l: clamp(p.l + 8, 0, 96) })}), hsl(${triplet(p)}) 55%, hsl(${triplet(dark)}))`,
  };

  if (input.secondary) {
    const sec = parseColor(input.secondary);
    out.secondary = triplet(sec);
    out.secondaryForeground = readableForeground(sec);
  }
  if (input.highlight) {
    const hi = parseColor(input.highlight);
    out.highlight = triplet(hi);
    out.highlightForeground = readableForeground(hi);
  }
  if (input.fontHeading) out.fontHeading = input.fontHeading;
  if (input.fontBody) out.fontBody = input.fontBody;
  if (input.fontLinks?.length) out.fontLinks = input.fontLinks;
  if (input.logoUrl) out.logoUrl = input.logoUrl;
  if (input.logoUrlDark) out.logoUrlDark = input.logoUrlDark;
  if (input.wordmark) out.wordmark = input.wordmark;
  if (input.radius) out.radius = input.radius;

  return out;
}

/** Convenience: the derived theme as a pretty JSON string to paste/seed. */
export function deriveThemeJson(input: DeriveThemeInput): string {
  return JSON.stringify(deriveTheme(input), null, 2);
}

export { toTriplet };
